import { useState, type FormEvent } from 'react';
import type { Hex } from 'viem';

import { MARKETS, txUrl } from '../config.ts';
import { cancelIntent, deadlineIn, newNonce, placeTakeProfit, run } from '../lib/intents.ts';
import { getSlippageBps, priceLimit } from '../lib/slippage.ts';
import { amountInput, fmtPx, fmtUsd, movePct, netPnl, parseUnits, toInput } from '../lib/format.ts';
import { blockAgeSec, closedNet, viewPosition } from '../lib/positions.ts';
import type { LivePosition } from '../lib/protocol.ts';
import { fillPx } from '../lib/protocol.ts';
import type { ApiClosed, IntentView, Transfer } from '../lib/relayer.ts';
import { errorMessage } from '../lib/tx.ts';
import { useApp } from '../state.tsx';
import { cardFromClosed, cardFromOpen } from './PnlCard.tsx';
import { ago, CoinIcon, compactUsd, tone } from './ui.tsx';

const MAX_CLOSE = 25;

function Empty({ children }: { children: string }) {
  return <div className="empty">{children}</div>;
}

export function PositionsTable() {
  const { positions, markets, globals, block, wallet, signer, toast, refresh, relayerDown, accountError, intents, openPnlCard } = useApp();
  const [closing, setClosing] = useState<Set<string>>(new Set());
  if (!wallet) return <Empty>Connect a wallet to see your positions</Empty>;
  if (relayerDown) return <Empty>{`Relayer unreachable (${accountError}). Positions are safe onchain.`}</Empty>;
  if (!positions) return <Empty>Loading…</Empty>;
  if (positions.length === 0) return <Empty>No open positions. Purr-fectly flat.</Empty>;

  async function close(ids: bigint[]) {
    if (!wallet || !signer) return;
    const keys = ids.map(String);
    setClosing((s) => new Set([...s, ...keys]));
    try {
      const bps = getSlippageBps();
      for (let i = 0; i < ids.length; i += MAX_CLOSE) {
        const chunk = ids.slice(i, i + MAX_CLOSE);
        // closing a long sells (floor), closing a short buys back (cap); no quote means a market close
        const limitPx = chunk.map((id) => {
          const p = positions!.find((x) => x.id === id);
          const px = p && fillPx(markets?.find((x) => x.id === p.instrumentId), !p.isLong);
          return p && px ? priceLimit(px, !p.isLong, bps) : 0n;
        });
        await run({ action: 'close', request: { user: wallet.address, positionIds: chunk, limitPx, nonce: newNonce(), deadline: deadlineIn() } }, signer);
      }
      // the realized-PnL toast comes from the account feed once the close is indexed
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: 'Close failed', body: errorMessage(e) });
    } finally {
      setClosing((s) => new Set([...s].filter((k) => !keys.includes(k))));
    }
  }

  return (
    <div className="table-wrap">
      <table className="grid-table">
        <thead>
          <tr>
            <th>Asset</th>
            <th className="r">Notional</th>
            <th className="r">Entry</th>
            <th className="r">Mark</th>
            <th className="r">PnL</th>
            <th className="r">%</th>
            <th className="r">Bust</th>
            <th className="r">Take Profit</th>
            <th>Risk</th>
            <th className="r">Margin</th>
            <th className="r">When</th>
            <th className="r">
              {positions.length > 1 && (
                <button className="link" disabled={closing.size > 0 || globals?.closePaused} onClick={() => close(positions.map((p) => p.id))}>
                  Close all
                </button>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => {
            const v = viewPosition(p, markets, globals);
            const scale = markets?.find((x) => x.id === p.instrumentId)?.inst.priceScale ?? 10;
            const busy = closing.has(String(p.id));
            return (
              <tr key={String(p.id)} className={p.isLong ? 'row-long' : 'row-short'}>
                <td>
                  <span className="asset">
                    <CoinIcon market={p.instrumentId} size={18} />
                    <b>{MARKETS[p.instrumentId]?.symbol}</b>
                    <span className={`side-label ${p.isLong ? 'pos' : 'neg'}`}>
                      {p.leverage.toString()}× {p.isLong ? 'LONG' : 'SHORT'}
                    </span>
                  </span>
                </td>
                <td className="r num strong">{compactUsd(v.notional)}</td>
                <td className="r num muted">{fmtPx(p.entryPx, scale)}</td>
                <td className="r num">{v.mark ? fmtPx(v.mark, scale) : '- -'}</td>
                <td className={`r num strong ${tone(v.net)}`}>{v.net !== undefined ? `${v.net < 0n ? '-' : '+'}$${fmtUsd(v.net < 0n ? -v.net : v.net)}` : '- -'}</td>
                <td className={`r num ${tone(v.net)}`}>{v.roe !== undefined ? `${v.roe.toFixed(1)}%` : '- -'}</td>
                <td className="r num">{v.bust ? fmtPx(v.bust, scale) : '- -'}</td>
                <td className="r">
                  <TakeProfitCell p={p} order={takeProfitOf(intents, p.id)} />
                </td>
                <td>
                  <span className="risk" title={v.risk !== undefined ? `${Math.round(v.risk * 100)}% of the way to bust` : ''}>
                    <span style={{ width: `${(v.risk ?? 0) * 100}%` }} className={v.risk !== undefined && v.risk > 0.75 ? 'hot' : ''} />
                  </span>
                </td>
                <td className="r num">{fmtUsd(p.margin)}</td>
                <td className="r muted">{p.openedBlock !== undefined && block !== undefined ? ago(blockAgeSec(block, p.openedBlock)!) : '- -'}</td>
                <td className="r nowrap">
                  <ShareButton onClick={() => openPnlCard(cardFromOpen(v, markets))} disabled={v.net === undefined} />
                  <button className="btn btn-ghost btn-xs" disabled={busy || globals?.closePaused} onClick={() => close([p.id])}>
                    {busy ? '…' : 'Close'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function HistoryTable({ rows }: { rows: ApiClosed[] }) {
  const { markets, globals, block, wallet, openPnlCard } = useApp();
  if (!wallet) return <Empty>Connect a wallet to see your history</Empty>;
  if (rows.length === 0) return <Empty>No closed positions yet</Empty>;
  return (
    <div className="table-wrap">
      <table className="grid-table">
        <thead>
          <tr>
            <th>Asset</th>
            <th className="r">Notional</th>
            <th className="r">Entry</th>
            <th className="r">Exit</th>
            <th className="r">PnL</th>
            <th className="r">%</th>
            <th className="r">Margin</th>
            <th className="r">Result</th>
            <th className="r">When</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => {
            const scale = markets?.find((m) => m.id === h.instrumentId)?.inst.priceScale ?? 10;
            const net = closedNet(h, globals);
            const at = blockAgeSec(block, h.blockNumber);
            return (
              <tr key={String(h.id)} className={h.isLong ? 'row-long' : 'row-short'}>
                <td>
                  <span className="asset">
                    <CoinIcon market={h.instrumentId} size={18} />
                    <b>{MARKETS[h.instrumentId]?.symbol}</b>
                    <span className={`side-label ${h.isLong ? 'pos' : 'neg'}`}>
                      {h.leverage.toString()}× {h.isLong ? 'LONG' : 'SHORT'}
                    </span>
                  </span>
                </td>
                <td className="r num">{compactUsd(h.margin * h.leverage)}</td>
                <td className="r num muted">{fmtPx(h.entryPx, scale)}</td>
                <td className="r num">{fmtPx(h.exitPx, scale)}</td>
                <td className={`r num strong ${tone(net)}`}>{`${net < 0n ? '-' : '+'}$${fmtUsd(net < 0n ? -net : net)}`}</td>
                <td className={`r num ${tone(net)}`}>{(Number((net * 10_000n) / h.margin) / 100).toFixed(1)}%</td>
                <td className="r num">{fmtUsd(h.margin)}</td>
                <td className="r">{h.outcome === 'liquidated' ? <span className="tag tag-liq">Liquidated</span> : <span className="tag">Closed</span>}</td>
                <td className="r muted" title={`block ${h.blockNumber}`}>
                  {at !== undefined ? ago(at) : `#${h.blockNumber}`}
                </td>
                <td className="r">
                  <ShareButton onClick={() => openPnlCard(cardFromClosed(h, markets, globals))} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Opens the shareable PnL card of a row */
function ShareButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button className="btn btn-ghost btn-xs share-btn" onClick={onClick} disabled={disabled} title="Share as an image" aria-label="Share PnL card">
      <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden>
        <path d="M8 1.5v8M4.5 5 8 1.5 11.5 5M2.5 9.5v4h11v-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export function ClaimsTable() {
  const { account, globals, wallet } = useApp();
  if (!wallet) return <Empty>Connect a wallet</Empty>;
  if (!account || account.claims.length === 0) return <Empty>No queued balances</Empty>;
  return (
    <div className="table-wrap">
      <p className="note">
        Wins closed while the house was short of liquidity, paid first-in first-out as it refills
        {globals ? ` ($${fmtUsd(globals.totalQueued, 0)} queued in total)` : ''}.
      </p>
      <table className="grid-table">
        <thead>
          <tr>
            <th>Claim</th>
            <th className="r">Still owed</th>
          </tr>
        </thead>
        <tbody>
          {account.claims.map((c) => (
            <tr key={String(c.id)}>
              <td className="num">#{c.id.toString()}</td>
              <td className="r num">${fmtUsd(c.owed)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACTION: Record<string, string> = {
  open: 'Open position',
  close: 'Close position',
  withdrawToCore: 'Withdraw to HyperCore',
  stake: 'Stake PAPURR',
  unstake: 'Unstake PAPURR',
  claim: 'Claim rewards',
  registerSessionKey: 'Enable one-click trading',
};

/** The resting (or triggered) take profit of a position, if any */
export function takeProfitOf(intents: IntentView[] | undefined, positionId: bigint): IntentView | undefined {
  const id = positionId.toString();
  return intents?.find(
    (i) => i.limit?.kind === 'tp' && i.limit.positionId === id && (i.status === 'waiting' || i.status === 'pending' || i.status === 'submitted'),
  );
}

/**
 * The take profit of one position: its price with a way to cancel it, or a small editor to set one. A
 * take profit closes the position once the price reaches it, at that price or better.
 */
function TakeProfitCell({ p, order }: { p: LivePosition; order?: IntentView }) {
  const { markets, globals, wallet, signer, session, toast } = useApp();
  const [text, setText] = useState<string>();
  const [busy, setBusy] = useState(false);
  const m = markets?.find((x) => x.id === p.instrumentId);
  const scale = m?.inst.priceScale ?? 10;
  const decimals = Math.round(Math.log10(scale));

  if (order) {
    return (
      <span className="tp-set">
        <span className="num pos">{fmtPx(BigInt(order.limit!.px), scale)}</span>
        {order.status === 'waiting' ? <CancelIntentButton id={order.id} label="✕" title="Cancel the take profit" /> : <span className="muted small"> closing…</span>}
      </span>
    );
  }
  if (text === undefined) {
    return (
      <button
        className="link small"
        disabled={!signer}
        title="Close this position automatically once the price reaches a target"
        // start 1% in the position's favour from the entry
        onClick={() => setText(toInput((p.entryPx * (p.isLong ? 101n : 99n)) / 100n, decimals))}
      >
        + TP
      </button>
    );
  }

  const px = parseUnits(text, decimals);
  // a take profit sits on the winning side of the entry
  const valid = !!px && (p.isLong ? px > p.entryPx : px < p.entryPx);
  const net = valid && m && globals ? netPnl({ entryPx: p.entryPx, exitPx: px!, margin: p.margin, leverage: p.leverage, isLong: p.isLong }, m.inst, globals.winFeeRate) : undefined;
  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (!valid || !wallet || !signer || busy) return;
    setBusy(true);
    try {
      await placeTakeProfit(wallet.address, p.id, px!, signer, session?.expiry);
      toast({ kind: 'ok', title: 'Take profit set', body: `Closes at $${fmtPx(px!, scale)} or better${net !== undefined ? `, about +$${fmtUsd(net)}` : ''}.` });
      setText(undefined);
    } catch (err) {
      toast({ kind: 'err', title: 'Could not set the take profit', body: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="tp-edit" onSubmit={save}>
      <input
        className="num"
        inputMode="decimal"
        autoComplete="off"
        autoFocus
        aria-label="Take profit price"
        title={net !== undefined ? `About +$${fmtUsd(net)} after fees` : p.isLong ? 'Above the entry' : 'Below the entry'}
        value={text}
        onChange={(e) => setText(amountInput(e.target.value))}
        onKeyDown={(e) => e.key === 'Escape' && setText(undefined)}
      />
      <button type="submit" className="link small" disabled={!valid || busy}>
        {busy ? '…' : 'Set'}
      </button>
      <button type="button" className="link small muted" onClick={() => setText(undefined)}>
        ✕
      </button>
    </form>
  );
}

/** "3d 4h", "5h 12m", "12m" until a unix time */
function until(deadline: number): string {
  const s = Math.max(0, deadline - Date.now() / 1000);
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

/** Resting limit orders: each opens once the price reaches its limit, at that price or better */
export function OrdersTable() {
  const { intents, markets, positions, wallet } = useApp();
  if (!wallet) return <Empty>Connect a wallet to see your orders</Empty>;
  const orders = (intents ?? []).filter((i) => i.limit && (i.status === 'waiting' || i.status === 'pending' || i.status === 'submitted'));
  if (orders.length === 0) return <Empty>No open orders. Pick Limit in the order form, or + TP on a position.</Empty>;
  return (
    <div className="table-wrap">
      <table className="grid-table">
        <thead>
          <tr>
            <th>Asset</th>
            <th className="r">Notional</th>
            <th className="r">Limit</th>
            <th className="r">Price Now</th>
            <th className="r">Distance</th>
            <th className="r">Margin</th>
            <th className="r">Expires</th>
            <th className="r" />
          </tr>
        </thead>
        <tbody>
          {orders.map((i) => {
            const o = i.limit!;
            const tp = o.kind === 'tp';
            const mk = markets?.find((x) => x.id === o.instrumentId);
            const scale = mk?.inst.priceScale ?? 10;
            const px = BigInt(o.px);
            // a take profit's size is its position's
            const pos = tp ? positions?.find((x) => x.id.toString() === o.positionId) : undefined;
            const margin = tp ? pos?.margin : BigInt(o.margin!);
            const leverage = tp ? pos?.leverage : BigInt(o.leverage!);
            // the side the order trades decides the band edge it is compared to: a long open buys at the top,
            // a long's take profit sells at the bottom
            const buys = tp ? !o.isLong : o.isLong;
            const now = fillPx(mk, buys);
            const dist = now ? movePct(now, px) : undefined;
            return (
              <tr key={i.id} className={o.isLong ? 'row-long' : 'row-short'}>
                <td>
                  <span className="asset">
                    <CoinIcon market={o.instrumentId} size={18} />
                    <b>{MARKETS[o.instrumentId]?.symbol}</b>
                    <span className={`side-label ${o.isLong ? 'pos' : 'neg'}`}>
                      {tp && 'TP · '}
                      {leverage !== undefined ? `${leverage}× ` : ''}
                      {o.isLong ? 'LONG' : 'SHORT'}
                    </span>
                  </span>
                </td>
                <td className="r num strong">{margin !== undefined && leverage !== undefined ? compactUsd(margin * leverage) : '- -'}</td>
                <td className="r num strong">{fmtPx(px, scale)}</td>
                <td className="r num">{now ? fmtPx(now, scale) : '- -'}</td>
                <td className="r num muted">{dist !== undefined ? `${dist > 0 ? '+' : ''}${dist.toFixed(2)}%` : '- -'}</td>
                <td className="r num">{margin !== undefined ? fmtUsd(margin) : '- -'}</td>
                <td className="r muted">{until(o.deadline)}</td>
                <td className="r">
                  {i.status === 'waiting' ? <CancelIntentButton id={i.id} /> : <span className="tag tag-pending">filling</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Takes a still-queued intent or a resting order back; the row turns `cancelled` with the next push */
function CancelIntentButton({ id, label = 'cancel', title }: { id: Hex; label?: string; title?: string }) {
  const { signer, toast } = useApp();
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="link small"
      title={title}
      disabled={busy || !signer}
      onClick={async () => {
        setBusy(true);
        try {
          await cancelIntent(id, signer!);
        } catch (e) {
          toast({ kind: 'err', title: 'Could not cancel', body: errorMessage(e) });
          setBusy(false);
        }
      }}
    >
      {' '}
      {busy ? '…' : label}
    </button>
  );
}

export function StatusList() {
  const { intents, wallet } = useApp();
  if (!wallet) return <Empty>Connect a wallet</Empty>;
  if (!intents || intents.length === 0) return <Empty>No signed actions yet</Empty>;
  return (
    <div className="table-wrap">
      <table className="grid-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Action</th>
            <th>Status</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {intents.map((i) => {
            const href = i.txHash ? txUrl(i.txHash) : undefined;
            return (
              <tr key={i.id}>
                <td className="muted num">{new Date(i.createdAt).toLocaleTimeString()}</td>
                <td>{i.limit ? (i.limit.kind === 'tp' ? 'Take profit' : `Limit ${i.limit.isLong ? 'long' : 'short'}`) : (ACTION[i.action] ?? i.action)}</td>
                <td>
                  <span className={`tag tag-${i.status}`}>{i.status}</span>
                  {i.status === 'pending' && i.ahead ? <span className="muted small"> · {i.ahead} ahead</span> : null}
                  {(i.status === 'pending' || i.status === 'waiting') && <CancelIntentButton id={i.id} />}
                </td>
                <td className="detail">
                  {i.error ??
                    (href ? (
                      <a href={href} target="_blank" rel="noreferrer">
                        {i.txHash!.slice(0, 10)}… ↗
                      </a>
                    ) : (
                      <span className="num muted">{i.txHash ? `${i.txHash.slice(0, 10)}…` : ''}</span>
                    ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const TRANSFER_LABEL: Record<Transfer['kind'], { label: string; route: string; inflow: boolean }> = {
  deposit: { label: 'Deposit', route: 'HyperEVM', inflow: true },
  coreDeposit: { label: 'Deposit', route: 'HyperCore', inflow: true },
  cctpDeposit: { label: 'Deposit', route: 'Another chain', inflow: true },
  withdraw: { label: 'Withdraw', route: 'HyperEVM', inflow: false },
  coreWithdraw: { label: 'Withdraw', route: 'HyperCore', inflow: false },
};

/** Status tag of a transfer; a deposit from another chain is bridging until the keeper finalizes it here */
function transferStatus(t: Transfer): { tag: string; text: string } {
  if (t.status === 'failed') return { tag: 'tag-failed', text: 'Failed' };
  if (t.status === 'done') return { tag: 'tag-succeeded', text: 'Done' };
  return { tag: 'tag-pending', text: t.kind === 'cctpDeposit' ? 'Bridging' : 'Crediting' };
}

/** Deposits and withdrawals of the trading balance; `limit` trims it for the funds dialog. */
export function TransfersTable({ limit }: { limit?: number }) {
  const { transfers, globals, block, wallet, relayerDown } = useApp();
  if (!wallet) return <Empty>Connect a wallet</Empty>;
  if (relayerDown) return <Empty>Relayer unreachable</Empty>;
  if (!transfers) return <Empty>Loading…</Empty>;
  if (transfers.length === 0) return <Empty>No deposits or withdrawals yet</Empty>;
  const rows = limit ? transfers.slice(0, limit) : transfers;
  return (
    <div className="table-wrap">
      <table className="grid-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Route</th>
            <th className="r">Amount</th>
            <th>Status</th>
            <th className="r">When</th>
            {!limit && <th className="r">Transaction</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const meta = TRANSFER_LABEL[t.kind];
            // a pending HyperCore deposit only knows its core amount until the credit lands
            const amount = t.status === 'pending' && t.coreWei !== undefined && globals ? t.coreWei * globals.coreScale : t.amount;
            // a burn on another chain has no block here yet: its wall-clock time instead
            const inFlight = t.blockNumber === 0n && t.timeMs !== undefined;
            const at = inFlight ? t.timeMs! / 1000 : blockAgeSec(block, t.blockNumber);
            const href = inFlight ? undefined : txUrl(t.txHash);
            const status = transferStatus(t);
            return (
              <tr key={`${t.txHash}-${t.kind}`} className={meta.inflow ? 'row-long' : 'row-short'}>
                <td className="strong">{meta.label}</td>
                <td className="muted">{t.source ? `From ${t.source}` : meta.route}</td>
                <td className={`r num strong ${meta.inflow ? 'pos' : 'neg'}`}>
                  {t.status === 'pending' ? '≈ ' : ''}
                  {meta.inflow ? '+' : '-'}${fmtUsd(amount)}
                </td>
                <td>
                  <span className={`tag ${status.tag}`} title={t.error}>
                    {status.text}
                  </span>
                </td>
                <td className="r muted" title={inFlight ? `burn ${t.txHash}` : `block ${t.blockNumber}`}>
                  {at !== undefined ? ago(at) : `#${t.blockNumber}`}
                </td>
                {!limit && (
                  <td className="r num">
                    {href ? (
                      <a href={href} target="_blank" rel="noreferrer">
                        {t.txHash.slice(0, 10)}… ↗
                      </a>
                    ) : (
                      <span className="muted">{t.txHash.slice(0, 10)}…</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
